import { useEffect, useRef, useState } from 'react';
import type { ReadingMode, Story } from '@/contracts/content';
import { Scene } from '@/content/scenes';
import { Button } from '@/design-system/Button';
import { cn } from '@/design-system/cn';
import { IconArrow, IconBack, IconSpeaker } from '@/features/studio/Icons';

interface Props {
  story: Story;
  /** Page ID, not an index — page zero is a real, persisted position (§19.1). */
  pageId: string | null;
  mode: ReadingMode;
  onPageChange(pageId: string): void;
  onFinish(): void;
  onExit(): void;
  onModeChange(mode: ReadingMode): void;
  /** Calm bedtime option (spec §8.4). Omitted where there is no profile. */
  bedtime?: boolean;
  onBedtimeChange?(on: boolean): void;
}

const MODE_LABEL: Record<ReadingMode, string> = {
  'listen-together': 'Listen together',
  'read-with-help': 'Read with help',
  'read-myself': 'Read myself',
};

/**
 * The reader (spec §7 C04).
 *
 * Controls stay in the same place on every page. There is no timer and no
 * forced pacing; the child can go back and reread freely. Narration uses the
 * device's own speech synthesis when a recorded track is not available, and
 * word highlighting is shown ONLY when the page carries accurate timings —
 * otherwise the sentence is simply read aloud (spec §7 C04).
 */
export function StoryReader({
  story, pageId, mode, onPageChange, onFinish, onExit, onModeChange, bedtime, onBedtimeChange,
}: Props) {
  const pages = story.currentVersion.pages;
  const index = Math.max(0, pages.findIndex((p) => p.id === pageId));
  const page = pages[index] ?? pages[0];
  const isLast = index === pages.length - 1;

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [textSize, setTextSize] = useState(28);
  const [speaking, setSpeaking] = useState(false);
  const utterRef = useRef<SpeechSynthesisUtterance | null>(null);

  const canSpeak = typeof window !== 'undefined' && 'speechSynthesis' in window;

  const stopSpeech = () => {
    if (canSpeak) window.speechSynthesis.cancel();
    setSpeaking(false);
  };

  const speak = (text: string) => {
    if (!canSpeak) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 0.82;
    u.pitch = 1.05;
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    utterRef.current = u;
    setSpeaking(true);
    window.speechSynthesis.speak(u);
  };

  // Auto-narrate in Listen together; never in Read myself (spec §7 C04).
  useEffect(() => {
    stopSpeech();
    if (mode === 'listen-together') {
      const t = setTimeout(() => speak(page.text), 320);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page.id, mode]);

  // Pause narration when the app is backgrounded (spec §7 C04).
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') stopSpeech();
    };
    document.addEventListener('visibilitychange', onHide);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      if (canSpeak) window.speechSynthesis.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A child tapping Next twice quickly must move two pages, not twice to the
  // same one. The parent owns `pageId`, so track the requested index locally
  // rather than recomputing from state that has not re-rendered yet.
  const pendingIndex = useRef(index);
  if (pages[pendingIndex.current]?.id !== pageId) pendingIndex.current = index;

  const go = (delta: number) => {
    const target = Math.min(pages.length - 1, Math.max(0, pendingIndex.current + delta));
    if (target === pendingIndex.current) return;
    pendingIndex.current = target;
    stopSpeech();
    onPageChange(pages[target].id);
  };

  return (
    <div className="flex h-[100dvh] flex-col bg-paper">
      <header className="flex shrink-0 items-center gap-m px-l py-m">
        <button
          onClick={() => {
            stopSpeech();
            onExit();
          }}
          className="flex min-h-touch min-w-touch items-center justify-center gap-xs rounded-pebble border-2 border-line bg-card px-m font-display text-[15px] font-semibold text-ink-soft"
        >
          <IconBack aria-hidden />
          <span className="max-[420px]:sr-only">Leave</span>
        </button>

        <div className="flex-1 text-center">
          <p className="font-display text-[14px] font-semibold text-ink-faint">
            Page {index + 1} of {pages.length}
          </p>
          <div className="mx-auto mt-xs flex max-w-[220px] gap-[3px]" aria-hidden>
            {pages.map((p, i) => (
              <span
                key={p.id}
                className={cn(
                  'h-[5px] flex-1 rounded-full transition-colors',
                  i <= index ? 'bg-magic' : 'bg-line',
                )}
              />
            ))}
          </div>
        </div>

        <button
          onClick={() => setSettingsOpen((v) => !v)}
          aria-expanded={settingsOpen}
          className="flex min-h-touch min-w-touch items-center justify-center rounded-pebble border-2 border-line bg-card px-m font-display text-[15px] font-semibold text-ink-soft"
        >
          Aa
        </button>
      </header>

      {settingsOpen ? (
        <div className="mx-l mb-m rounded-pebble border-2 border-line bg-card p-l shadow-cut">
          <h2 className="mb-m font-display text-[16px] font-semibold">Reading settings</h2>
          <div className="mb-l">
            <label className="mb-s block font-display text-[14px] font-semibold text-ink-soft" htmlFor="textsize">
              Text size
            </label>
            <input
              id="textsize"
              type="range"
              min={20}
              max={44}
              step={2}
              value={textSize}
              onChange={(e) => setTextSize(Number(e.target.value))}
              className="h-touch w-full accent-magic"
            />
          </div>
          <fieldset>
            <legend className="mb-s font-display text-[14px] font-semibold text-ink-soft">
              How would you like to read?
            </legend>
            <div className="flex flex-wrap gap-s">
              {(Object.keys(MODE_LABEL) as ReadingMode[]).map((m) => (
                <button
                  key={m}
                  onClick={() => onModeChange(m)}
                  aria-pressed={mode === m}
                  className={cn(
                    'min-h-touch rounded-pebble border-2 px-l font-display text-[15px] font-semibold',
                    mode === m ? 'border-primary bg-primary-soft text-primary-deep' : 'border-line bg-card text-ink-soft',
                  )}
                >
                  {MODE_LABEL[m]}
                </button>
              ))}
            </div>
          </fieldset>

          {onBedtimeChange ? (
            <div className="u-hairline mt-l pt-l">
              <div className="flex items-start gap-m">
                <input
                  id="bedtime-toggle"
                  type="checkbox"
                  checked={!!bedtime}
                  onChange={(e) => onBedtimeChange(e.target.checked)}
                  className="mt-1 h-6 w-6 shrink-0 accent-magic"
                />
                <label htmlFor="bedtime-toggle" className="cursor-pointer">
                  <span className="block font-display text-[15px] font-semibold text-ink">
                    Bedtime
                  </span>
                  <span className="mt-xs block text-[14px] text-ink-soft">
                    Softer colours, sparkles off, and an offer to finish making a picture
                    tomorrow instead of tonight.
                  </span>
                </label>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      <main className="flex min-h-0 flex-1 flex-col gap-l overflow-y-auto px-l pb-l lg:flex-row lg:items-center lg:gap-xl">
        <figure className="lg:flex-[1.15]">
          <div className="overflow-hidden rounded-blob border-2 border-line shadow-cut-lg">
            <Scene
              id={page.sceneId}
              title={page.alt}
              className="aspect-[3/2] w-full"
            />
          </div>
        </figure>

        <div className="lg:flex-1">
          {/* Story text stays real, selectable text — never burned into the
              illustration (spec §4.1). */}
          <p
            className="font-story leading-[1.5] text-ink"
            style={{ fontSize: `${textSize}px` }}
          >
            {page.text}
          </p>

          {mode !== 'listen-together' ? (
            <button
              onClick={() => (speaking ? stopSpeech() : speak(page.text))}
              className="mt-l inline-flex min-h-touch items-center gap-s rounded-pebble border-2 border-line bg-card px-l font-display text-[15px] font-semibold text-ink-soft"
            >
              <IconSpeaker aria-hidden />
              {speaking ? 'Stop reading' : 'Read this page to me'}
            </button>
          ) : null}
        </div>
      </main>

      <footer className="flex shrink-0 items-center gap-m border-t-2 border-line bg-card px-l py-m pb-[max(12px,env(safe-area-inset-bottom))]">
        <Button
          variant="outline"
          size="lg"
          onClick={() => go(-1)}
          disabled={index === 0}
          icon={<IconBack />}
        >
          Back
        </Button>

        <div className="flex-1 text-center">
          {canSpeak ? (
            <button
              onClick={() => (speaking ? stopSpeech() : speak(page.text))}
              className="inline-flex min-h-touch items-center gap-s rounded-full border-2 border-line px-l font-display text-[15px] font-semibold text-ink-soft"
            >
              <IconSpeaker aria-hidden />
              {speaking ? 'Pause' : 'Play'}
            </button>
          ) : null}
        </div>

        {isLast ? (
          <Button variant="magic" size="lg" onClick={onFinish} iconRight={<IconArrow />}>
            Explore the story
          </Button>
        ) : (
          <Button variant="primary" size="lg" onClick={() => go(1)} iconRight={<IconArrow />}>
            Next
          </Button>
        )}
      </footer>
    </div>
  );
}
